/**
 * Blockly ワークスペースをブラウザ内で近似実行するインタプリタ。
 *
 * Studio が生成する Lua の semantics（`src/blocks/*.ts` の generator）に
 * 挙動を合わせる。物理・Humanoid に依存するブロックは graceful skip する。
 *
 * 設計:
 * - 文ブロックの実行は async（control_wait / forever / repeat の中断のため）。
 * - 中断は AbortSignal で行う。signal.aborted が立った時点で各ステップが早期 return する。
 * - self（実行主体の objectId）はインタプリタ生成時に固定する。
 */

/** wait / forever などの中断に使う最小限の実行コンテキスト。 */
export interface ExecutionContext {
  /** 中断要求の検知に使う。 */
  readonly signal: AbortSignal
  /** メッセージ送信（events_sendMessage）。Runtime 側が配送を担う。 */
  readonly dispatchMessage: (message: string) => void
  /**
   * self オブジェクトの破棄を Runtime に通知する（control_destroy）。
   * Runtime は同一オブジェクトの全ハンドラ（別 onPlay/onMessage 等）を中断する。
   */
  readonly destroySelf: () => void
  /**
   * 実行時生成オブジェクト（control_clone のクローン等）へ、テンプレート名に
   * 学習者が付けたプログラムを起動する（Roblox の Clone() が配下 Script ごと
   * 複製され実行が始まる挙動の再現。例: RJF09 のボールは各クローンが乱数で
   * 散らばり、接触で消えるプログラムを自分でも実行する）。
   */
  readonly startObjectPrograms?: (
    objectId: string,
    templateName: string,
  ) => void
  /**
   * control_clone でクローンを 1 つ生成してよいか問い合わせ、許可されたら生成予算を
   * 1 消費する（戻り値 true）。1 プレイの生成上限（MAX_DYNAMIC_CLONES）に達していれば
   * false を返し、呼び出し側はクローンを生成しない。forever/repeat 内のクローン生成で
   * World が際限なく膨らみページが無反応化するのを防ぐ安全網。
   */
  readonly tryConsumeCloneBudget?: () => boolean
  /**
   * プレイ全体で共有する単一クロックに基づく中断可能な待機（RunSession.wait）。
   * 環境ビヘイビア（BehaviorContext.wait）と同じクロックを使うことで、白ブロック
   * （このインタプリタ）と緑ブロック（ビヘイビア）の wait が個別 setTimeout チェーンの
   * 積み重ね誤差により相対的にずれていく問題を防ぐ。未設定時は delay() にフォールバック。
   */
  readonly wait?: (seconds: number) => Promise<void>
}

/** 中断時に投げる内部例外。スタックを巻き戻して実行を止める。 */
export class AbortError extends Error {
  constructor() {
    super('execution aborted')
    this.name = 'AbortError'
  }
}

export const isAbortError = (error: unknown): error is AbortError =>
  error instanceof AbortError

/** signal が中断済みなら AbortError を投げる。 */
export const throwIfAborted = (signal: AbortSignal): void => {
  if (signal.aborted) {
    throw new AbortError()
  }
}

/**
 * 中断可能な待機。指定秒数待つか、signal が中断されたら即座に解決する。
 */
export const delay = (seconds: number, signal: AbortSignal): Promise<void> =>
  new Promise<void>((resolve) => {
    if (signal.aborted) {
      resolve()
      return
    }
    const timer = setTimeout(
      () => {
        signal.removeEventListener('abort', onAbort)
        resolve()
      },
      Math.max(0, seconds * 1000),
    )
    const onAbort = (): void => {
      clearTimeout(timer)
      resolve()
    }
    signal.addEventListener('abort', onAbort, { once: true })
  })

/**
 * 中断応答性と協調的インターリーブのための yield 間隔（命令数）。
 * wait を含まない長いスタックや大きな repeat でも、この命令数ごとに制御を譲る。
 */
export const YIELD_EVERY_STATEMENTS = 200

/**
 * Lua generator が forever/while の各周回末尾に挿入する `wait()`（引数なし）の
 * 待機秒数。Roblox の legacy wait() は 30Hz スロットルで最低約 1/30 秒待つため、
 * 「ずっと＋1度回す」が実機と同じ約 30 度/秒になるよう合わせる。
 */
export const LUA_WAIT_SECONDS = 1 / 30
